import React, { useState, useEffect } from 'react'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import SeveranceNotice from './SeveranceNotice'
import { severanceWarning, type GroupMember, type PropertyGroupRow } from '@/lib/groups'
import { apiFetch, apiErrorMessage } from '@/lib/csrf'
import { AlertCircle, Loader2, Unlink } from 'lucide-react'

export interface DecoupleResult {
  headline: string
  severed: Parameters<typeof SeveranceNotice>[0]['severed']
  subject: string
}

interface DecoupleMemberDialogProps {
  /**
   * `/a/:account_slug/settings` — the business's settings root, served by the backend
   * (`currentAccount.settingsPath`). Every endpoint below is this plus a FIXED suffix.
   */
  settingsPath: string
  group: PropertyGroupRow | null
  member: GroupMember | null
  onClose: () => void
  onDone: (result: DecoupleResult) => void
}

// REMOVING ONE FOLLOWING LISTING FROM A COMBINED INVENTORY: the most consequential control on this
// page.
//
// Two things have to be true of this dialog before it may be confirmed:
//
//   1. IT STATES THE UNPRICED CONSEQUENCE BEFORE IT HAPPENS (locked product rule 4). Any room that
//      pointed at a parent in another hotel becomes independent, and an independent room with no
//      base rate is not priced at all. SeveranceNotice names every one of those rooms, on both sides
//      of the link, and says which will actually be left without a price.
//   2. IT IS DELIBERATE. When rooms WILL be left unpriced, the operator types the hotel's name to
//      confirm — the same bar the app sets for any action whose consequence outlives the click. When
//      nothing is severed there is nothing to be careful about, so the extra step is not imposed:
//      a confirmation ritual attached to a harmless act is what makes operators stop reading them.
//
// What is NOT scary, and is said plainly first: nothing is deleted and the hotel can be attached
// again at any time. Its own saved settings, seasons, guardrails and targets were never removed,
// only ignored while the lead governed — they simply resume.
export default function DecoupleMemberDialog({ settingsPath, group, member, onClose, onDone }: DecoupleMemberDialogProps) {
  const [confirmation, setConfirmation] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setConfirmation('')
    setError(null)
    setSubmitting(false)
  }, [member?.id])

  const severed = member?.decouplePreview?.severed ?? []
  const warning = severanceWarning(severed)
  // The typed confirmation is required exactly when something will actually go unpriced.
  const requiresTypedName = warning.tone === 'warning'
  const nameMatches = member ? confirmation.trim().toLowerCase() === member.name.toLowerCase() : false
  const canConfirm = !!member && !submitting && (!requiresTypedName || nameMatches)
  const lastMember = member?.decouplePreview?.groupDestroyed ?? false

  async function remove() {
    if (!group || !member) return
    setError(null)
    setSubmitting(true)

    try {
      const response = await apiFetch(`${settingsPath}/groups/${group.id}/members/${member.id}`, { method: 'DELETE' })
      const data = await response.json()

      if (!response.ok) {
        setError(data.errors?.join(', ') || 'This listing could not be removed. Try again.')
        return
      }

      onDone({ headline: data.headline, severed: data.severed ?? [], subject: data.propertyName ?? member.name })
    } catch (e) {
      setError(apiErrorMessage(e, 'Network error. Please check your connection and try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AlertDialog open={member !== null} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent className="sm:max-w-[540px]">
        <AlertDialogHeader>
          {/* The combined inventory is called by its lead listing's name (`group.name` is the lead's), so
              the lead itself, removable only as the last listing, is not named twice. */}
          <AlertDialogTitle>
            {member?.isLead || lastMember
              ? `Remove ${member?.name} from its combined inventory?`
              : `Remove ${member?.name} from the combined inventory led by ${group?.name}?`}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {member?.name} goes back to pricing on its own. Its own base rates, seasons, day-of-week rules, guardrails
            and targets, kept untouched while it was a following listing, apply again. Nothing is deleted, and you can
            add it again at any time.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogBody className="space-y-4">
          {lastMember && (
            <p className="rounded-lg border border-border/60 bg-muted/40 px-3.5 py-2.5 text-xs text-muted-foreground">
              {member?.name} is the last listing in this combined inventory, so the combined inventory is removed with
              it.
            </p>
          )}

          <SeveranceNotice severed={severed} mode="before" subject={member?.name ?? 'this listing'} />

          {requiresTypedName && (
            <div className="space-y-2">
              <Label htmlFor="decouple-confirm" className="text-xs">
                Type <span className="font-semibold text-foreground">{member?.name}</span> to confirm
              </Label>
              <Input
                id="decouple-confirm"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                placeholder={member?.name}
                autoComplete="off"
              />
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </AlertDialogBody>

        <AlertDialogFooter>
          <Button type="button" variant="ghost" className="rounded-lg" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="rounded-lg"
            onClick={remove}
            disabled={!canConfirm}
            aria-busy={submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Removing…
              </>
            ) : (
              <>
                <Unlink className="mr-1.5 h-3.5 w-3.5" />
                Remove listing
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
